import api from "./axios";
import { API_URL } from "../constants/constants";

// Asking a browser to notify this reader, and telling the server where to send.
//
// The chat has always shown notifications from the page itself, which cannot
// work on a phone: Chrome on Android refuses new Notification() and iOS only
// delivers to an installed web app. Both need a service worker, and a
// notification that arrives while the app is closed needs a push subscription
// as well. This arranges both, and answers why it could not rather than failing
// silently, because "notifications do not work" is otherwise impossible to tell
// apart from "the reader said no".

export const SW_PATH = "/sw.js";

export const supported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// A VAPID key travels as base64url and has to reach the browser as bytes.
export const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
};

// On iOS none of this exists until the reader has added the app to their home
// screen, and there is no way to ask for that except to say so.
export const needsInstallFirst = () => {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent || "");
  const installed =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;
  return ios && !installed;
};

export const registerServiceWorker = async () => {
  if (!supported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (error) {
    console.error("push: the service worker would not register", error);
    return null;
  }
};

// Returns why, so a caller can tell the reader something true.
export const enablePushNotifications = async () => {
  if (!supported()) return { ok: false, reason: "unsupported" };
  if (needsInstallFirst()) return { ok: false, reason: "install-first" };
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };

  const registration = await registerServiceWorker();
  if (!registration) return { ok: false, reason: "no-service-worker" };

  if (Notification.permission !== "granted") {
    const answer = await Notification.requestPermission();
    if (answer !== "granted") return { ok: false, reason: "denied" };
  }

  try {
    await navigator.serviceWorker.ready;
    // An existing subscription is reused. Asking for a second one with a
    // different key throws, and re-sending the same one keeps the server's
    // record pointing at whoever is signed in now.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const { data } = await api.get(`${API_URL}/api/v1/push/public-key`);
      if (!data?.publicKey) return { ok: false, reason: "not-configured" };
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }
    const body = subscription.toJSON();
    await api.post(`${API_URL}/api/v1/push/subscribe`, {
      endpoint: body.endpoint,
      keys: body.keys,
    });
    return { ok: true };
  } catch (error) {
    console.error("push: could not subscribe", error);
    return { ok: false, reason: "subscribe-failed" };
  }
};
